import { useEffect, useMemo, useRef, useState } from "react"
import type { Attribute } from "../../api/types"
import { useEscape } from "../../hooks/useEscape"

/**
 * Пустая ячейка справа от последней колонки: вписываете атрибут — появляется колонка.
 *
 * Список висит на `position: fixed` и закрывается при прокрутке: у таблицы своя
 * горизонтальная прокрутка, и обычный выпадающий список она бы обрезала.
 */
export default function AddColumn({
	available,
	busy,
	onAdd,
}: {
	available: Attribute[]
	busy: boolean
	onAdd: (attributeId: number) => void
}) {
	const [query, setQuery] = useState("")
	const [open, setOpen] = useState(false)
	const [active, setActive] = useState(0)
	const [anchor, setAnchor] = useState<DOMRect | null>(null)
	const input = useRef<HTMLInputElement>(null)
	const drop = useRef<HTMLDivElement>(null)

	const found = useMemo(() => {
		const needle = query.trim().toLowerCase()
		if (!needle) return available
		return available.filter(item => item.name.toLowerCase().includes(needle))
	}, [available, query])

	const close = () => {
		setOpen(false)
		setAnchor(null)
	}

	// прокрутка уводит поле из-под списка — проще закрыть, чем пересчитывать.
	// Прокрутку самого списка пропускаем: он длинный, и закрывать его нельзя.
	useEffect(() => {
		if (!open) return
		const onScroll = (event: Event) => {
			const target = event.target as Node | null
			if (target && drop.current?.contains(target)) return
			close()
		}
		window.addEventListener("scroll", onScroll, true)
		window.addEventListener("resize", close)
		return () => {
			window.removeEventListener("scroll", onScroll, true)
			window.removeEventListener("resize", close)
		}
	}, [open])

	// Esc закрывает список и очищает набранное. Хук — до раннего возврата ниже:
	// порядок хуков между рендерами меняться не должен
	useEscape(open, () => {
		setQuery("")
		close()
	})

	const show = () => {
		const box = input.current?.getBoundingClientRect()
		if (!box) return
		setAnchor(box)
		setActive(0)
		setOpen(true)
	}

	const add = (attributeId: number) => {
		setQuery("")
		close()
		onAdd(attributeId)
	}

	if (available.length === 0) {
		return <span className="muted tp-addcol__empty">все атрибуты добавлены</span>
	}

	return (
		<>
			<input
				ref={input}
				className="tp-addcol__input"
				placeholder="+ колонка"
				title="Выберите атрибут из списка или начните вводить название"
				value={query}
				disabled={busy}
				onFocus={show}
				onBlur={close}
				onChange={event => {
					setQuery(event.target.value)
					setActive(0)
					if (!open) show()
				}}
				onKeyDown={event => {
					if (event.key === "ArrowDown") {
						event.preventDefault()
						setActive(index => Math.min(index + 1, found.length - 1))
					}
					if (event.key === "ArrowUp") {
						event.preventDefault()
						setActive(index => Math.max(index - 1, 0))
					}
					if (event.key === "Enter" && found[active]) {
						event.preventDefault()
						add(found[active].id)
					}
				}}
			/>

			{open && anchor && (
				<div
					ref={drop}
					className="tp-drop"
					style={{
						top: anchor.bottom + 4,
						// список шире ячейки — прижимаем к правому краю поля
						left: Math.max(8, anchor.right - 320),
					}}
					// не отдаём фокус, иначе onBlur закроет список раньше клика
					onMouseDown={event => event.preventDefault()}
				>
					<div className="tp-drop__head">
						<span>Атрибуты, которых нет в таблице</span>
						<span className="muted">{found.length}</span>
					</div>

					<div className="tp-drop__list">
						{found.map((item, index) => (
							<button
								key={item.id}
								type="button"
								className={
									"tp-drop__item" +
									(index === active ? " tp-drop__item--on" : "")
								}
								onMouseEnter={() => setActive(index)}
								onClick={() => add(item.id)}
							>
								<span className="tp-drop__name">{item.name}</span>
								<span className="tp-drop__id mono muted">{item.id}</span>
							</button>
						))}
						{found.length === 0 && (
							<div className="tp-drop__empty">Ничего не найдено</div>
						)}
					</div>

					<div className="tp-drop__foot">
						↑ ↓ — выбрать, Enter — добавить колонку, Esc — закрыть список
					</div>
				</div>
			)}
		</>
	)
}
